import React, { useEffect, useState } from 'react';
import {Link, useNavigate} from "react-router-dom";
import SocialLoginFragment from "@app/site/authentication/fragment/SocialRegistrationFragment";
import useToaster from "@app/component/overlays/toaster/ToasterHook";
import {useTranslation} from "@app/component/translation/Translation";
import {useForm} from "react-hook-form";
import RegistrationInput from "@app/model/registration/input/RegistrationInput";
import FormInputText from "@app/component/form/input/text/FormInputText";
import FormInputPhone from "@app/component/form/input/phone/FormInputPhone";
import {Switch} from "@headlessui/react";
import FormSwitch from "@app/component/form/input/switch/FormSwitch";
import FormButton from "@app/component/form/button/FormButton";
import CountryAutocompleteField from "@app/component/form/input/autocomplete/CountryAutocompleteField";
import RegistrationService from "@app/model/registration/service/RegistrationService";
import {useMutation} from "@tanstack/react-query";
import useSiteState from "@app/state/SiteState";


const SignUpPage = React.memo(() => {

    const siteState = useSiteState();

    const form = useForm();
    const {t} = useTranslation();
    const toaster = useToaster();
    const navigate = useNavigate();
    const hasAcceptedTerms = form.watch('acceptTerms');
    const [isPending, setIsPending] = useState<boolean>();

    useEffect(() => {
        siteState.logout();
    }, []);

    const registerMutation = useMutation({
        mutationFn: RegistrationService.register,
        onSuccess: () => {
            navigate('/verification-pending')
        },
        onError: (error: string) => {
            console.log(error)
            if (error == 'server.exception.registration.PASSWORD_DOES_NOT_VALIDATE') {
                form.setError("password", {message: t(error)})
            } else
                toaster.error('Registration', t(error));

        }
    })

    const onSubmit = (data: RegistrationInput) => {
        registerMutation.mutate(data)
    }

    return <div className="min-h-full bg-white">
        <div className="flex lg:h-dvh min-w-max content-center justify-center flex-1">
            <div className="flex flex-1 flex-col justify-center px-6 py-12">
                <div className="mx-auto w-full max-w-sm lg:w-96">
                    <div>
                        <img
                            className="h-10 w-auto"
                            src="/assets/img/brand/logo-icon.png"
                            alt="RoomPulse"
                        />
                        <h2 className="mt-8 text-2xl font-bold leading-9 tracking-tight text-gray-900">
                            Create a new account
                        </h2>
                        <p className="mt-2 text-sm leading-6 text-gray-500">
                            Already registered?{' '}
                            <Link to="/sign-in" className="font-semibold text-blue-600 hover:text-blue-500"
                                  title="Click to sign in to your account">Click to sign in</Link>
                        </p>
                    </div>

                    <div className="mt-10">
                        <div>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                                <div className="grid grid-cols-1 gap-y-4 gap-x-8">
                                    <FormInputText name='name' form={form} label='Name' isRequired
                                                   placeholder="Insert your name"/>
                                    <FormInputText name='email'
                                                   validation={RegistrationService.checkEmailOrUsernameExists()}
                                                   form={form} placeholder="Insert your email"
                                                   label='Email' isRequired/>
                                    <FormInputText name='companyName' form={form} label='Company'
                                                   placeholder="Insert your company name" isRequired/>
                                    <CountryAutocompleteField name="countryId" form={form} label="Country" isRequired
                                                              placeholder="Insert your country"/>

                                    <FormInputPhone namePrefix='phone' form={form} label='Phone Number' isRequired
                                                    placeholder="Insert your phone number"/>
                                    <FormInputText name='password' type='password' form={form} label='Password'
                                                   placeholder="Insert your password"
                                                   isRequired/>
                                </div>


                                <div className="mt-8">
                                    <Switch.Group as="div" className="flex gap-x-4 sm:col-span-2 mt-3">
                                        <div className="flex h-6 items-center">
                                            <FormSwitch name='acceptTerms' form={form}
                                                        label='Agree to the policy'/>
                                            <Switch.Label className="text-sm leading-6 text-slate-600 ml-2">
                                                By selecting this, you agree to our{' '}
                                                <a href="/sdsd"
                                                   className="font-semibold text-blue-600 hover:text-blue-500">privacy&nbsp;policy</a>.
                                            </Switch.Label>
                                        </div>
                                    </Switch.Group>
                                    <FormButton type='submit' state='primary' extraClassName='flex w-full mt-4'
                                                isLoading={registerMutation.isPending || isPending} disabled={!hasAcceptedTerms}>
                                        Sign up</FormButton>
                                </div>

                            </form>
                        </div>

                        <div className="mt-10">
                            <SocialLoginFragment onPending={setIsPending}/>
                        </div>
                    </div>
                </div>
            </div>
            <div className="relative hidden flex-1 lg:block">
                <img
                    className="absolute inset-0 h-full w-full object-cover"
                    src="https://images.unsplash.com/photo-1587783456120-fa80c81a6c4c?q=80&w=2574&auto=format&fit=crop&ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D"
                    alt=""
                />
            </div>
        </div>
    </div>
})

export default SignUpPage;